import React, { useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { X, Loader2 } from "lucide-react";
import ImageUpload from "./ImageUpload";

const CATEGORIES = ["Travel Tips", "Visa Updates", "Destination Guides", "Honeymoon", "Luxury", "News"];
const EMPTY = { title: "", excerpt: "", content: "", category: "Travel Tips", image: "", author: "Acchajee Editorial", published: true };

export default function BlogFormModal({ initial, onClose, onSaved }) {
  const [f, setF] = useState(initial ? { ...EMPTY, ...initial } : EMPTY);
  const [loading, setLoading] = useState(false);

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    setF({ ...f, [name]: type === "checkbox" ? checked : value });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!f.title || !f.image) { toast.error("Title and image are required"); return; }
    setLoading(true);
    try {
      if (initial) await api.patch(`/admin/blog/${initial.id}`, f);
      else await api.post("/admin/blog", f);
      toast.success(initial ? "Post updated" : "Post published");
      onSaved(); onClose();
    } catch { toast.error("Save failed"); }
    finally { setLoading(false); }
  };

  const input = "w-full px-4 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-[#006CE4] focus:ring-4 focus:ring-blue-100 transition";
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" data-testid="blog-form-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-slate-100">
          <h3 className="h3">{initial ? "Edit Blog Post" : "New Blog Post"}</h3>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-full" data-testid="blog-form-close"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={submit} className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2"><label className="text-xs font-semibold muted">Title *</label><input required name="title" value={f.title} onChange={onChange} className={input} data-testid="blog-form-title" /></div>
          <div><label className="text-xs font-semibold muted">Category</label>
            <select name="category" value={f.category} onChange={onChange} className={input} data-testid="blog-form-category">
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div><label className="text-xs font-semibold muted">Author</label><input name="author" value={f.author} onChange={onChange} className={input} data-testid="blog-form-author" /></div>
          <div className="sm:col-span-2">
            <label className="text-xs font-semibold muted">Cover Image *</label>
            <ImageUpload value={f.image} onChange={(url) => setF({ ...f, image: url })} testId="blog-form-image" />
          </div>
          <div className="sm:col-span-2"><label className="text-xs font-semibold muted">Excerpt (short summary)</label><textarea name="excerpt" value={f.excerpt} onChange={onChange} rows="2" className={`${input} resize-none`} data-testid="blog-form-excerpt" /></div>
          <div className="sm:col-span-2"><label className="text-xs font-semibold muted">Content (plain text — line breaks preserved)</label><textarea name="content" value={f.content} onChange={onChange} rows="10" className={`${input} resize-none`} data-testid="blog-form-content" /></div>
          <label className="sm:col-span-2 flex items-center gap-2 text-sm"><input type="checkbox" name="published" checked={!!f.published} onChange={onChange} className="accent-[#FF7D00]" data-testid="blog-form-published" /> Published (visible on site)</label>
          <div className="sm:col-span-2 flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="btn-outline flex-1 justify-center" data-testid="blog-form-cancel">Cancel</button>
            <button type="submit" disabled={loading} className="btn-primary flex-1 justify-center disabled:opacity-60" data-testid="blog-form-submit">{loading ? <Loader2 className="w-4 h-4 animate-spin" /> : initial ? "Save Changes" : "Publish Post"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
